import React, { useEffect, useState } from 'react';
import SettingsTabs from '../layout/SettingsTabs';
import { getAiModels } from '../../api/services/aiService';
import { fmt, UsageBar, useAiUsage } from './AiUsage';

const STATUS_TEXT = { ok: 'On track', warning: 'Running low', exceeded: 'Quota reached' };
const STATUS_BADGE = { ok: 'text-bg-success', warning: 'text-bg-warning', exceeded: 'text-bg-danger' };

export default function AiUsagePage() {
  const { usage, error, reload } = useAiUsage();
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { getAiModels().then(setCatalog).catch(() => setCatalog(null)); }, []);

  return (
    <div>
      <h1 className="h3 mb-1">Settings</h1>
      <p className="text-muted mb-3">AI replies and models for this workspace.</p>
      <SettingsTabs />

      {error && !usage && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-link p-0" onClick={reload}>Retry</button></div>}
      {!usage && !error && <p className="text-muted" role="status">Loading AI usage…</p>}

      {usage && (
        <div className="row g-4">
          <div className="col-lg-7">
            <section className="card p-4 h-100" aria-label="AI replies this month">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h2 className="h5 mb-1">AI replies — {usage.periodLabel}</h2>
                  <div className="text-muted small">{usage.plan} plan · resets on {new Date(usage.resetsOn).toLocaleDateString('en-US', { day: 'numeric', month: 'long', timeZone: 'UTC' })}</div>
                </div>
                <span className={`badge ${STATUS_BADGE[usage.status]}`}>{STATUS_TEXT[usage.status]}</span>
              </div>
              <div className="display-6 fw-bold mb-1">{fmt(usage.used)}<small className="fs-6 text-muted"> / {fmt(usage.quota)} replies</small></div>
              <UsageBar usage={usage} />
              <div className="d-flex justify-content-between text-muted small mt-2"><span>{usage.percent}% used</span><span>{fmt(usage.remaining)} left</span></div>
              {usage.status !== 'ok' && (
                <div className={`alert ${usage.status === 'exceeded' ? 'alert-danger' : 'alert-warning'} mt-3 mb-0`} role="alert">
                  {usage.status === 'exceeded'
                    ? 'Your AI replies are used up. New visitor chats are handed to a human agent until the quota resets. Upgrade your plan for a higher quota.'
                    : 'You have used most of this month’s AI replies. When they run out, new chats go to a human agent.'}
                </div>
              )}
              <h3 className="h6 mt-4">By bot</h3>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead><tr><th>Bot</th><th>Model</th><th className="text-end">Replies</th></tr></thead>
                  <tbody>
                    {usage.byBot.length === 0 && <tr><td colSpan={3} className="text-muted">No bots yet.</td></tr>}
                    {usage.byBot.map((b) => <tr key={b.botId}><td>{b.name}</td><td>{b.model}</td><td className="text-end">{fmt(b.used)}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="col-lg-5">
            <section className="card p-4 h-100" aria-label="Available models">
              <h2 className="h5 mb-1">Models on your plan</h2>
              <p className="text-muted small">ChatBizz provides the AI — you don’t need an OpenAI account or API key. Choose a model for each bot under Bot Configuration → Model &amp; AI.</p>
              {!catalog && <p className="text-muted small">Loading models…</p>}
              {catalog && (
                <ul className="list-unstyled mb-0">
                  {catalog.models.map((m) => (
                    <li key={m.id} className="d-flex justify-content-between gap-3 py-2 border-top">
                      <div><strong>{m.label}</strong><div className="small text-muted">{m.description}</div></div>
                      <span className={`badge align-self-start ${m.available ? 'text-bg-success' : 'text-bg-secondary'}`}>{m.available ? 'Included' : `${m.minPlan} plan`}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
